import React, { useState, useEffect } from 'react';
import { Download, FileText } from 'lucide-react';
import api from '../../services/api';
import { formatDate, getFileIcon } from '../../utils/helpers';
import toast from 'react-hot-toast';

export default function ViewTranscript({ caseData }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!caseData?.id) return;
      try {
        const response = await api.documents.list(caseData.id);
        setDocuments(Array.isArray(response) ? response : (response.documents || response.data || []));
      } catch (error) {
        console.error('Failed to load documents:', error);
      } finally {
        setLoading(false);
      }
    };
    loadDocuments();
  }, [caseData?.id]);

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const response = await api.documents.download(docId);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const caseFields = [
    { label: 'Student Name', value: caseData.applicant_name },
    { label: 'College Name', value: caseData.college_name },
    { label: 'Applied For', value: caseData.apply_for },
    { label: 'Apply Date', value: caseData.apply_date ? formatDate(caseData.apply_date) : null },
    { label: 'Delivery To', value: caseData.delivery_type === 'email'
        ? (caseData.delivery_address ? `Email: ${caseData.delivery_address}` : 'Email')
        : caseData.delivery_address || null },
  ];

  return (
    <div className="transcript-view">
      <div className="transcript-section">
        <h3 className="transcript-section-title">Case Details</h3>
        <div className="transcript-fields">
          {caseFields.map((field, index) => (
            <div key={index} className="transcript-field" style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span className="field-label" style={{ fontWeight: '600', color: '#374151', minWidth: '140px', flexShrink: 0 }}>{field.label}</span>
              <span className="field-value" style={{ color: '#6b7280', marginLeft: '16px' }}>{field.value || 'N/A'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="transcript-section">
        <h3 className="transcript-section-title">Status Information</h3>
        <div className="transcript-fields">
          <div className="transcript-field" style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span className="field-label" style={{ fontWeight: '600', color: '#374151', minWidth: '140px', flexShrink: 0 }}>Current Status</span>
            <span className="field-value" style={{ color: '#6b7280', marginLeft: '16px' }}>{caseData.status || 'N/A'}</span>
          </div>
          <div className="transcript-field" style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span className="field-label" style={{ fontWeight: '600', color: '#374151', minWidth: '140px', flexShrink: 0 }}>Last Updated</span>
            <span className="field-value" style={{ color: '#6b7280', marginLeft: '16px' }}>{formatDate(caseData.updated_at)}</span>
          </div>
        </div>
      </div>

      <div className="transcript-section">
        <h3 className="transcript-section-title">Documents</h3>
        {loading ? (
          <div className="skeleton" style={{ height: '100px' }}></div>
        ) : documents.length === 0 ? (
          <p className="text-gray">No documents uploaded</p>
        ) : (
          <div className="documents-grid">
            {documents.map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-icon">
                  {getFileIcon(doc.file_name)}
                </div>
                <div className="document-info">
                  <p className="document-name">{doc.file_name}</p>
                  <p className="document-type">{doc.document_type}</p>
                  <p className="document-date">{formatDate(doc.created_at)}</p>
                </div>
                <button
                  className="btn btn-icon"
                  onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                  title="Download"
                >
                  <Download size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
